import * as React from "react";
import { cn } from "../lib/utils.ts";

export interface PosterCardProps extends React.HTMLAttributes<HTMLAnchorElement> {
  href: string;
  title: string;
  posterUrl: string;
  posterVariants?: {
    avif?: Partial<Record<"w320" | "w640" | "w960" | "w1280", string>>;
    webp?: Partial<Record<"w320" | "w640" | "w960" | "w1280", string>>;
  };
  progress?: number;
  badge?: string;
  /** TMDB-style 0–10 rating, shown in the corner badge + on hover. */
  rating?: number;
  /** Short synopsis revealed on hover (clamped to 3 lines). */
  synopsis?: string;
  /** Category slugs / labels rendered as chips on hover. */
  categories?: string[];
}

const WIDTHS = [
  { key: "w320", w: 320 },
  { key: "w640", w: 640 },
  { key: "w960", w: 960 },
  { key: "w1280", w: 1280 },
] as const;

function buildSrcset(map: Partial<Record<string, string>> | undefined): string | undefined {
  if (!map) return undefined;
  const parts: string[] = [];
  for (const v of WIDTHS) {
    const url = map[v.key];
    if (url) parts.push(`${url} ${v.w}w`);
  }
  return parts.length ? parts.join(", ") : undefined;
}

function formatCategory(slug: string): string {
  return slug
    .split(/[-_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Series poster tile. Aspect-ratio reserved to prevent CLS (Section 10.1).
 * AVIF first, WebP fallback, raw posterUrl as the last resort.
 * progress 0–100 renders the "continue watching" bar at the bottom.
 *
 * On hover (or keyboard focus) reveals a Netflix-style info overlay with
 * the title, rating, category chips and a 3-line synopsis preview.
 */
export const PosterCard = React.forwardRef<HTMLAnchorElement, PosterCardProps>(
  (
    {
      href,
      title,
      posterUrl,
      posterVariants,
      progress,
      badge,
      rating,
      synopsis,
      categories,
      className,
      ...props
    },
    ref,
  ) => {
    const avifSrcset = buildSrcset(posterVariants?.avif);
    const webpSrcset = buildSrcset(posterVariants?.webp);
    const ratingLabel = typeof rating === "number" && rating > 0 ? rating.toFixed(1) : null;
    const chips = (categories ?? []).slice(0, 3);
    return (
      <a
        ref={ref}
        href={href}
        aria-label={title}
        className={cn(
          "group relative block overflow-hidden rounded-lg bg-[var(--color-muted)]",
          "transition-transform duration-[var(--duration-fast)] ease-[var(--ease-out)]",
          "hover:z-20 hover:scale-[1.05] focus-visible:z-20 focus-visible:scale-[1.05]",
          "hover:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.6)]",
          "focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2",
          className,
        )}
        style={{ aspectRatio: "var(--aspect-poster)" }}
        {...props}
      >
        <picture>
          {avifSrcset && (
            <source
              type="image/avif"
              srcSet={avifSrcset}
              sizes="(max-width: 768px) 33vw, (max-width: 1280px) 20vw, 16vw"
            />
          )}
          {webpSrcset && (
            <source
              type="image/webp"
              srcSet={webpSrcset}
              sizes="(max-width: 768px) 33vw, (max-width: 1280px) 20vw, 16vw"
            />
          )}
          <img
            src={posterUrl}
            alt=""
            decoding="async"
            className="h-full w-full object-cover transition-opacity duration-[var(--duration-base)] group-hover:opacity-95"
          />
        </picture>

        {badge && (
          <span className="absolute left-2 top-2 rounded-full bg-[var(--color-accent)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-accent-foreground)]">
            {badge}
          </span>
        )}

        {ratingLabel && (
          <span
            className={cn(
              "absolute right-2 top-2 flex items-center gap-1",
              "rounded-full bg-black/65 px-2 py-0.5 text-[11px] font-semibold text-white",
              "backdrop-blur transition-opacity duration-[var(--duration-base)]",
              "opacity-90 group-hover:opacity-0",
            )}
            aria-label={`Rating ${ratingLabel} out of 10`}
          >
            <StarIcon />
            {ratingLabel}
          </span>
        )}

        {typeof progress === "number" && progress > 0 && (
          <div
            role="progressbar"
            aria-valuenow={Math.min(100, Math.max(0, progress))}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${title} watched ${Math.round(progress)}%`}
            className="absolute inset-x-2 bottom-2 h-1 rounded-full bg-black/60"
          >
            <div
              className="h-full rounded-full bg-[var(--color-accent)]"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}

        {/* Hover preview — info card stacked over a darkening gradient. */}
        <div
          className={cn(
            "pointer-events-none absolute inset-0 flex flex-col justify-end gap-2 p-3",
            "bg-gradient-to-t from-black/95 via-black/70 to-black/10",
            "opacity-0 transition-opacity duration-[var(--duration-base)]",
            "group-hover:opacity-100 group-focus-visible:opacity-100",
          )}
        >
          <h3 className="line-clamp-2 text-sm font-semibold text-white drop-shadow">{title}</h3>

          {(ratingLabel || chips.length > 0) && (
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-white/85">
              {ratingLabel && (
                <span className="flex items-center gap-1 font-semibold text-amber-300">
                  <StarIcon />
                  {ratingLabel}
                </span>
              )}
              {ratingLabel && chips.length > 0 && <span className="text-white/30">·</span>}
              {chips.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-white/20 px-1.5 py-px text-[10px] uppercase tracking-wide text-white/85"
                >
                  {formatCategory(c)}
                </span>
              ))}
            </div>
          )}

          {synopsis && (
            <p className="line-clamp-3 text-[11px] leading-snug text-white/75">{synopsis}</p>
          )}
        </div>
      </a>
    );
  },
);
PosterCard.displayName = "PosterCard";

function StarIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className="-mt-px"
    >
      <path d="M12 2.5l2.95 6.96 7.55.6-5.73 5.05L18.5 22 12 18.27 5.5 22l1.73-6.89L1.5 10.06l7.55-.6L12 2.5z" />
    </svg>
  );
}
